import React, { useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import ReviewsBar from "../../components/ReviewsBar";
import LiveStrip from "../../components/LiveStrip";
import EmaLiveOpsSection from "../../components/ema/EmaLiveOpsSection";
import "../../styles/parkingVsTaxi.css";

const useCompactMobile = (maxWidth = 768) => {
  const getMatch = () => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
    return window.matchMedia(`(max-width: ${maxWidth}px)`).matches;
  };
  const [isCompact, setIsCompact] = useState(getMatch);

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return undefined;
    const mq = window.matchMedia(`(max-width: ${maxWidth}px)`);
    const onChange = (e) => setIsCompact(!!e.matches);
    setIsCompact(mq.matches);
    if (typeof mq.addEventListener === "function") {
      mq.addEventListener("change", onChange);
      return () => mq.removeEventListener("change", onChange);
    }
    mq.addListener(onChange);
    return () => mq.removeListener(onChange);
  }, [maxWidth]);

  return isCompact;
};

export default function EastMidlandsDropoff() {
  const isCompact = useCompactMobile();
  const [form, setForm] = useState({
    terminal: "EMA",
    journey_type: "DROPOFF",
    planned_minutes: 8,
    vehicle_type: "car",
    blue_badge: false,
    blue_badge_registered: false
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const autoSubmitted = useRef(false);
  const changeTimer = useRef(null);

  const clampMinutes = (value) => {
    const num = Number(value);
    if (Number.isNaN(num)) return 8;
    return Math.min(10080, Math.max(1, num));
  };

  const money = (v) => (v == null ? "—" : `£${Number(v).toFixed(2)}`);

  const buildResult = () => {
    const mins = clampMinutes(form.planned_minutes);

    const rapidCost = mins <= 15 ? 5 : mins <= 30 ? 5 + (mins - 15) : null;

    const shortCost = (() => {
      if (form.blue_badge && mins <= 30) return 0;
      if (mins <= 30) return 6;
      if (mins <= 60) return 8;
      if (mins <= 120) return 12;
      if (mins <= 180) return 16;
      return 16 + (mins - 180);
    })();

    const longCost = mins <= 60 ? 0 : mins - 60;

    const options = [
      {
        id: "RAPID_DROP_OFF",
        title: "Rapid Drop Off",
        tagline: "Drop off only",
        cost: rapidCost,
        warnings:
          mins > 30
            ? [
                "Maximum stay is 30 minutes.",
                "Non-payment can trigger a £100 Parking Charge Notice (reduced to £60 if paid within 14 days)."
              ]
            : [
                "Up to 15 minutes: £5, then £1 per minute.",
                "Payments must be made online by midnight the following day."
              ],
        infoNotes: [
          "Most suitable for quick drop offs, with check-in just minutes away.",
          "No barrier payment on exit; cash and cheque are not accepted."
        ]
      },
      {
        id: "SHORT_STAY",
        title: "Short Stay 1 Car Park",
        tagline: "Pick up or drop off",
        cost: shortCost,
        warnings:
          mins > 180
            ? ["Published tariff shown up to 3 hours; over-3-hour value is extrapolated at £1/min for guidance."]
            : [],
        infoNotes: [
          "Follow signs for 'Short Stay Car Park'.",
          "Ideal if you want to accompany passengers or go into the terminal to meet someone.",
          "Blue Badge parking is available in Short Stay 1 (free for first 30 minutes).",
          "Short Stay 1 can be accessed by walking to/from the terminal."
        ]
      },
      {
        id: "LONG_STAY",
        title: "Long Stay 2 Car Park",
        tagline: "Pick up or drop off",
        cost: longCost,
        warnings: [],
        infoNotes: [
          "Free for up to 60 minutes, then £1/min.",
          "Please press for a token at entry; payment at exit is card only if you overstay.",
          "15–20 min walk or shuttle bus.",
          "Shuttle buses run every 20 minutes, 24/7, except from 1 Nov 2025 to 31 Mar 2026 (unavailable)."
        ]
      }
    ];

    const byId = Object.fromEntries(options.map((o) => [o.id, o]));

    // Mirror Stansted-like recommendation UX with time-window logic first.
    let recommendedId = "RAPID_DROP_OFF";
    let why = "For short drop-offs, Rapid Drop Off is the quickest option.";

    if (mins <= 15) {
      recommendedId = "RAPID_DROP_OFF";
      why = "For very short stays, Rapid Drop Off keeps walking to a minimum.";
    } else if (mins <= 30) {
      recommendedId = form.blue_badge ? "SHORT_STAY" : "SHORT_STAY";
      why = form.blue_badge
        ? "Blue Badge can make Short Stay 1 free up to 30 minutes."
        : "For 16–30 mins, Short Stay 1 is usually better value and avoids Rapid Drop Off per-minute escalation.";
    } else if (mins <= 60) {
      recommendedId = "LONG_STAY";
      why = "Long Stay 2 is free up to 60 minutes and safer for timing uncertainty.";
    } else {
      const ranked = [byId.SHORT_STAY, byId.LONG_STAY]
        .filter(Boolean)
        .sort((a, b) => (a.cost ?? Number.POSITIVE_INFINITY) - (b.cost ?? Number.POSITIVE_INFINITY));
      recommendedId = ranked[0]?.id || "LONG_STAY";
      why =
        recommendedId === "LONG_STAY"
          ? "After 60 mins, Long Stay 2 often remains the lower-cost option."
          : "For this duration, Short Stay 1 is currently the cheaper option.";
    }

    return {
      journey: {
        terminal: "East Midlands",
        journey_type: "DROPOFF",
        planned_minutes: mins,
        blue_badge: form.blue_badge
      },
      options,
      recommendation: {
        best: recommendedId,
        why
      }
    };
  };

  const submit = async () => {
    setLoading(true);
    setError(null);
    try {
      setResult(buildResult());
    } catch (e) {
      setError("Failed to calculate East Midlands drop-off costs. Please try again.");
    }
    setLoading(false);
  };

  useEffect(() => {
    if (autoSubmitted.current) return;
    autoSubmitted.current = true;
    submit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!autoSubmitted.current) return;
    if (changeTimer.current) clearTimeout(changeTimer.current);
    changeTimer.current = setTimeout(() => {
      submit();
    }, 320);
    return () => {
      if (changeTimer.current) clearTimeout(changeTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);

  const cards = useMemo(() => {
    const map = {};
    (Array.isArray(result?.options) ? result.options : []).forEach((o) => {
      map[o.id] = o;
    });

    return [
      {
        key: "rapid",
        opt: map.RAPID_DROP_OFF,
        bg: "#f9e7b4",
        text: "#3b1f00"
      },
      {
        key: "short",
        opt: map.SHORT_STAY,
        bg: "#dcefff",
        text: "#0b233f"
      },
      {
        key: "long",
        opt: map.LONG_STAY,
        bg: "#e6fff1",
        text: "#0f3c2f"
      }
    ];
  }, [result]);

  const recommendedId = result?.recommendation?.best;
  const recommendedOpt = (result?.options || []).find((o) => o.id === recommendedId) || null;

  const renderOptionCard = (opt) => {
    if (!opt) return null;
    const isBest = result?.recommendation?.best === opt.id;
    const isUnavailable = opt.id === "RAPID_DROP_OFF" && Number(result?.journey?.planned_minutes || 0) > 30;

    return (
      <div
        key={opt.id}
        style={{
          border: isUnavailable ? "1px solid #d1d5db" : "1px solid #e5e7eb",
          borderRadius: 12,
          padding: 14,
          marginBottom: 12,
          background: isUnavailable ? "#f3f4f6" : isBest ? "#ecfdf5" : "#fff",
          opacity: isUnavailable ? 0.85 : 1
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
          <div style={{ fontWeight: 800 }}>{opt.title}</div>
          <div style={{ fontWeight: 800 }}>{isUnavailable ? "Not available" : money(opt.cost)}</div>
        </div>

        <div style={{ marginTop: 8, fontSize: 13, color: "#374151" }}>{opt.tagline}</div>
        {isUnavailable && (
          <div style={{ marginTop: 8, fontSize: 13, fontWeight: 700, color: "#92400e" }}>
            Not allowed over 30 minutes in Rapid Drop Off.
          </div>
        )}

        {Array.isArray(opt.warnings) && opt.warnings.length > 0 && (
          <ul style={{ marginTop: 10, paddingLeft: 18, lineHeight: 1.5 }}>
            {opt.warnings.map((w, i) => (
              <li key={`${opt.id}-w-${i}`}>{w}</li>
            ))}
          </ul>
        )}

        {Array.isArray(opt.infoNotes) && opt.infoNotes.length > 0 && (
          <ul style={{ marginTop: 10, paddingLeft: 18, lineHeight: 1.5 }}>
            {opt.infoNotes.map((n, i) => (
              <li key={`${opt.id}-n-${i}`}>{n}</li>
            ))}
          </ul>
        )}
      </div>
    );
  };

  const renderOptionSection = (opt, title, defaultOpen = false) => {
    if (!opt) return null;
    const idMap = {
      RAPID_DROP_OFF: "rapid_drop_off",
      SHORT_STAY: "short_stay",
      LONG_STAY: "long_stay"
    };

    return (
      <details id={`collapse-${idMap[opt.id]}`} className="train-collapsible" open={defaultOpen} style={{ marginTop: 12 }}>
        <summary className="train-summary-bar">{title}</summary>
        <div className="train-collapsible-body">{renderOptionCard(opt)}</div>
      </details>
    );
  };

  return (
    <div className="parking-wrapper">
      <Helmet>
        <title>East Midlands Drop-off (2026): Fees, Free Options &amp; PCN Rules</title>
        <meta
          name="description"
          content="Compare East Midlands Rapid Drop Off, Short Stay 1 and Long Stay 2 charges by minutes stayed, including Blue Badge and payment deadlines."
        />
        <meta
          name="keywords"
          content="East Midlands drop off charge, EMA Rapid Drop Off, East Midlands Short Stay 1 prices, East Midlands Long Stay free parking, East Midlands Blue Badge parking, EMA drop off penalty, East Midlands pick up and drop off"
        />
        <link rel="canonical" href="https://www.cheapcabgrab.com/east-midlands-airport/drop-off-calculator" />

        <meta property="og:title" content="East Midlands Drop-off (2026): Fees, Free Options &amp; PCN Rules" />
        <meta
          property="og:description"
          content="Compare Rapid Drop Off, Short Stay 1 and Long Stay 2 at East Midlands Airport. See payment deadlines, Blue Badge rules and PCN risk."
        />
        <meta property="og:url" content="https://www.cheapcabgrab.com/east-midlands-airport/drop-off-calculator" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: [
              {
                "@type": "Question",
                name: "How much is East Midlands Rapid Drop Off?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Rapid Drop Off is £5 for up to 15 minutes, then £1 per minute, with a maximum stay of 30 minutes.",
                },
              },
              {
                "@type": "Question",
                name: "When do I need to pay the East Midlands Rapid Drop Off charge?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Payment must be made by midnight the day after your visit, online via the payment portal or by phone.",
                },
              },
              {
                "@type": "Question",
                name: "What happens if I do not pay the Rapid Drop Off charge?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "If payment is not made by the deadline, a £100 Parking Charge may be issued, reduced to £60 if paid within 14 days.",
                },
              },
              {
                "@type": "Question",
                name: "Can I pay for East Midlands Rapid Drop Off in advance?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "No. Rapid Drop Off payment is made after visiting. You can set up Autopay to charge automatically after each journey.",
                },
              },
              {
                "@type": "Question",
                name: "Is there a free pick-up or drop-off option at East Midlands Airport?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Yes. Long Stay 2 is free for up to 60 minutes, then charged at £1 per minute after the free period.",
                },
              },
              {
                "@type": "Question",
                name: "What are East Midlands Short Stay 1 charges?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Short Stay 1 charges are £6 up to 30 minutes, £8 up to 1 hour, £12 up to 2 hours, and £16 up to 3 hours.",
                },
              },
              {
                "@type": "Question",
                name: "Do Blue Badge concessions apply at East Midlands pick-up and drop-off?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Blue Badge concession in this calculator applies to Short Stay 1 where the first 30 minutes are free.",
                },
              },
              {
                "@type": "Question",
                name: "How can I contact support for East Midlands Drop Off payments?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "For payment issues, contact APCOA via webchat through the payment portal or by phone on 03452 221517.",
                },
              },
            ],
          })}
        </script>

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: "East Midlands Pick Up and Drop Off Calculator",
            url: "https://www.cheapcabgrab.com/east-midlands-airport/drop-off-calculator",
            description:
              "Compare East Midlands Airport Rapid Drop Off, Short Stay 1 and Long Stay 2 by planned stay duration, payment deadline and Blue Badge rules.",
            inLanguage: "en-GB",
          })}
        </script>

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": ["SoftwareApplication", "WebApplication"],
            name: "East Midlands Drop-off Calculator",
            applicationCategory: "TravelApplication",
            operatingSystem: "All",
            description:
              "Dynamic calculator for East Midlands Airport drop-off and pick-up options. Compares Rapid Drop Off, Short Stay 1 and Long Stay 2 using planned minutes and published fee logic.",
            featureList: [
              "Rapid Drop Off max-stay risk highlighting",
              "Short Stay and Long Stay duration-based comparison",
              "Blue Badge concession handling for Short Stay 1",
              "Payment deadline and parking charge guidance",
            ],
            offers: {
              "@type": "Offer",
              price: "0",
              priceCurrency: "GBP",
            },
          })}
        </script>
      </Helmet>

      <LiveStrip variant="east-midlands" />

      <div className="parking-header" style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
        <h1 style={{ fontSize: isCompact ? 32 : undefined, lineHeight: isCompact ? 1.08 : undefined, marginBottom: isCompact ? 8 : undefined }}>
          🚗 East Midlands Pick Up and Drop Off Options
        </h1>
        <div
          style={{
            marginTop: 6,
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            marginLeft: 0,
            padding: isCompact ? "4px 8px" : "4px 10px",
            background: "#ecfeff",
            border: "1px solid #a5f3fc",
            borderRadius: 999,
            fontSize: isCompact ? 10.5 : 11,
            fontWeight: 700,
            lineHeight: 1.2,
            color: "#0f172a",
          }}
        >
          Updated: 21st Apr 2026 • Drop-off charges and rules
        </div>
        <p style={{ marginTop: 10, marginBottom: 0, fontSize: isCompact ? 15 : undefined, lineHeight: isCompact ? 1.35 : undefined }}>
          {isCompact ? (
            <>Compare <strong>parking options</strong> using official charges.</>
          ) : (
            <>Compare <strong>Rapid Drop Off</strong>, <strong>Short Stay 1</strong> and <strong>Long Stay 2</strong> using official parking charges.</>
          )}
        </p>
        <p style={{ marginTop: 6, marginBottom: 0, fontSize: isCompact ? 14 : undefined, lineHeight: isCompact ? 1.3 : undefined }}>
          Going to EMA? Use our{" "}
          <a href="/east-midlands-airport/live-journey-planner" style={{ fontWeight: 800 }}>
            live journey planner
          </a>
          .
        </p>
      </div>

      <div className="parking-form">
        <div className="minutes-card" style={{ marginTop: 4 }}>
          <div className="minutes-label-row">
            <label style={{ margin: 0 }} htmlFor="planned_minutes">
              Duration of stay (mins)
            </label>
            <span className="minutes-chip">Time</span>
          </div>
          <div className="minutes-input-row">
            <input
              type="number"
              className="parking-input"
              min="1"
              max="10080"
              step="1"
              id="planned_minutes"
              value={form.planned_minutes}
              onChange={(e) =>
                setForm({
                  ...form,
                  planned_minutes: e.target.value === "" ? "" : Math.min(10080, Math.max(1, Number(e.target.value)))
                })
              }
              onBlur={() => {
                setForm({ ...form, planned_minutes: clampMinutes(form.planned_minutes) });
              }}
            />
            <div className="minutes-nudge">
              <button
                type="button"
                onClick={() => setForm({ ...form, planned_minutes: clampMinutes((Number(form.planned_minutes) || 0) - 1) })}
              >
                −
              </button>
              <button
                type="button"
                onClick={() => setForm({ ...form, planned_minutes: clampMinutes((Number(form.planned_minutes) || 0) + 1) })}
              >
                +
              </button>
            </div>
          </div>
        </div>

        <label
          style={{
            display: "inline-flex",
            alignItems: "center",
            marginTop: 8,
            gap: isCompact ? 6 : 8,
            fontSize: isCompact ? 11.5 : 13,
            lineHeight: 1.2,
            whiteSpace: isCompact ? "normal" : "nowrap",
            flexWrap: isCompact ? "wrap" : "nowrap",
          }}
        >
          <input
            type="checkbox"
            checked={form.blue_badge}
            onChange={(e) => setForm({ ...form, blue_badge: e.target.checked })}
            style={{ width: isCompact ? 16 : undefined, height: isCompact ? 16 : undefined, flex: "0 0 auto" }}
          />
          <span style={{ display: "inline-block" }}>Blue Badge: Short Stay free ≤30 mins</span>
        </label>

        <button onClick={submit} className="parking-btn">
          {loading ? "Calculating…" : "Calculate best option"}
        </button>

        {error && <p style={{ color: "red", marginTop: 10 }}>{error}</p>}
      </div>

      {result && (
        <div className="parking-results">
          <div
            style={{
              borderRadius: 18,
              padding: 18,
              marginBottom: 16,
              color: "#fff",
              background: "#0b132b",
              boxShadow: "0 12px 30px rgba(0,0,0,0.16)"
            }}
          >
            <div style={{ fontWeight: 800, fontSize: 13, letterSpacing: 0.4, textTransform: "uppercase", opacity: 0.9 }}>
              East Midlands Drop-off (2026)
            </div>
            <div style={{ fontSize: 22, fontWeight: 800, marginTop: 6 }}>
              Recommendation: {recommendedOpt?.title || "—"}
            </div>
            <div style={{ fontSize: 14, marginTop: 6, opacity: 0.9 }}>
              Cost for your stay ({recommendedOpt?.title || "Option"}): <strong>{money(recommendedOpt?.cost)}</strong>
            </div>
            <div style={{ fontSize: 13, marginTop: 6, opacity: 0.9 }}>Why: {result.recommendation?.why}</div>

            <div
              style={{
                marginTop: 12,
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                gap: 10
              }}
            >
              {cards.map(({ key, opt, bg, text }) => {
                const isRecommended = opt?.id === recommendedId;
                const isUnavailable = opt?.id === "RAPID_DROP_OFF" && Number(result?.journey?.planned_minutes || 0) > 30;
                const map = {
                  RAPID_DROP_OFF: "rapid_drop_off",
                  SHORT_STAY: "short_stay",
                  LONG_STAY: "long_stay"
                };
                return (
                  <div
                    key={key}
                    style={{
                      border: "1px solid rgba(0,0,0,0.08)",
                      borderRadius: 14,
                      padding: 14,
                      background: isUnavailable ? "#e5e7eb" : bg,
                      color: isUnavailable ? "#374151" : text,
                      display: "flex",
                      flexDirection: "column",
                      gap: 6,
                      minHeight: 140,
                      position: "relative",
                      opacity: isUnavailable ? 0.8 : 1
                    }}
                  >
                    {isRecommended && !isUnavailable && (
                      <div
                        style={{
                          position: "absolute",
                          top: 10,
                          right: 10,
                          background: "#0f172a",
                          color: "#fff",
                          padding: "4px 10px",
                          borderRadius: 999,
                          fontSize: 11,
                          fontWeight: 800
                        }}
                      >
                        Recommended
                      </div>
                    )}
                    <div style={{ fontSize: 17, fontWeight: 800 }}>{opt?.title || "Awaiting option"}</div>
                    <div style={{ fontSize: 16, fontWeight: 800 }}>{isUnavailable ? "Not available" : opt?.cost != null ? money(opt.cost) : "—"}</div>
                    <div style={{ fontSize: 12, lineHeight: 1.4, opacity: 0.85 }}>
                      {opt?.tagline || "Compare safety, speed, and price."}
                    </div>
                    {isUnavailable && <div style={{ fontSize: 12, fontWeight: 700 }}>Max stay 30 mins in Rapid Drop Off.</div>}
                    {Array.isArray(opt?.warnings) && opt.warnings[0] && (
                      <div style={{ fontSize: 12, color: isUnavailable ? "#4b5563" : text }}>{`⚠️ ${opt.warnings[0]}`}</div>
                    )}
                    <div style={{ marginTop: "auto" }}>
                      <button
                        type="button"
                        onClick={() => {
                          const id = map[opt?.id];
                          if (!id) return;
                          const el = document.getElementById(`collapse-${id}`);
                          if (el) {
                            el.open = true;
                            requestAnimationFrame(() => {
                              el.scrollIntoView({ behavior: "smooth", block: "start" });
                            });
                          }
                        }}
                        style={{
                          marginTop: 8,
                          background: "rgba(255,255,255,0.5)",
                          border: "1px solid rgba(0,0,0,0.08)",
                          color: text,
                          padding: "6px 10px",
                          borderRadius: 10,
                          cursor: "pointer",
                          fontWeight: 700,
                          fontSize: 12,
                          width: "100%"
                        }}
                      >
                        View details ▸
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            <div style={{ fontSize: 12, marginTop: 8, opacity: 0.85 }}>
              Terminal: <strong>{result.journey?.terminal}</strong> • Type: <strong>{result.journey?.journey_type}</strong> • Planned
              time: <strong>{result.journey?.planned_minutes} mins</strong>
              {result.journey?.blue_badge ? " • Blue Badge" : ""}
            </div>
          </div>

          {(() => {
            const map = {};
            (Array.isArray(result.options) ? result.options : []).forEach((o) => {
              map[o.id] = o;
            });

            return (
              <>
                {renderOptionSection(map.RAPID_DROP_OFF, "Rapid Drop Off", false)}
                {renderOptionSection(map.SHORT_STAY, "Short Stay 1", false)}
                {renderOptionSection(map.LONG_STAY, "Long Stay 2", false)}
              </>
            );
          })()}

          <EmaLiveOpsSection />

          <details className="train-collapsible" style={{ marginTop: 12 }}>
            <summary className="train-summary-bar">ℹ️ What this tool checks (East Midlands)</summary>
            <div className="train-collapsible-body">
              <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.5 }}>
                <li>Rapid Drop Off: £5 up to 15 mins, then £1/min, max 30 mins.</li>
                <li>Short Stay 1: £6 (30 mins), £8 (1h), £12 (2h), £16 (3h).</li>
                <li>Blue Badge concession applies in Short Stay 1 only (free up to 30 mins).</li>
                <li>Long Stay 2: free up to 60 mins, then £1/min.</li>
                <li>Rapid Drop Off payment deadline: midnight day after visit.</li>
                <li>For Short Stay durations over 3 hours, this tool shows a guidance estimate.</li>
                <li>Barrierless flow, PCN risk, and key support links.</li>
              </ul>
            </div>
          </details>

          <details className="train-collapsible" style={{ marginTop: 12 }}>
            <summary className="train-summary-bar">❓ Rapid Drop Off payment FAQ</summary>
            <div className="train-collapsible-body">
              <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.6 }}>
                <li>Pay online via the payment portal, or by phone: <a href="tel:03452221517">03452 221517</a>.</li>
                <li>Cash and cheque are not accepted.</li>
                <li>There are no payment barriers in Rapid Drop Off; payment is made after your visit.</li>
                <li>Payment deadline is midnight the day after your visit.</li>
                <li>If unpaid, a £100 Parking Charge may be issued, reduced to £60 if paid within 14 days.</li>
                <li>You cannot pay in advance for a Rapid Drop Off visit.</li>
                <li>You can set up an account with a saved card (Autopay) for automatic charging per journey.</li>
                <li>If you cannot pay online, contact APCOA webchat or call <a href="tel:03452221517">03452 221517</a>.</li>
                <li>Parking Charge appeals are handled via APCOA.</li>
              </ul>
            </div>
          </details>

          <details className="train-collapsible" style={{ marginTop: 12 }}>
            <summary className="train-summary-bar">♿ Pre-book &amp; Blue Badge options at EMA</summary>
            <div className="train-collapsible-body">
              <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.6 }}>
                <li>Pre-book options include Short Stay, Long Stay, and Rapid Drop Off access planning.</li>
                <li>Rapid Drop Off moved to a barrierless system from 1 May 2025.</li>
                <li>Meet &amp; Greet: 1-2 minute walk; Blue Badge holders can receive discounted booking support by phone.</li>
                <li>Short Stay 1: 4-6 minute walk; Blue Badge parking available and first 30 minutes free.</li>
                <li>Long Stay 1/2: Blue Badge bays available with terminal transfer options.</li>
                <li>Mid Stay 3: Blue Badge bays available; terminal access by walk/shuttle arrangements.</li>
                <li>Pick Up &amp; Drop Off: designated Blue Badge bays available, including Rapid Drop Off marked bays.</li>
              </ul>
            </div>
          </details>

          <details className="train-collapsible" style={{ marginTop: 12 }}>
            <summary className="train-summary-bar">🆘 Need help with East Midlands drop-off or parking?</summary>
            <div className="train-collapsible-body">
              <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.6 }}>
                <li>
                  📞 APCOA Customer Services: <a href="tel:03452221517">03452 221517</a>
                </li>
                <li>
                  💳 Payment portal: <a href="https://www.eastmidlandsairport.com/parking/pick-up-and-drop-off/" target="_blank" rel="noreferrer">Pay Drop Off charge</a>
                </li>
                <li>
                  💬 APCOA support: live webchat via payment portal
                </li>
                <li>
                  🧾 Appeals: <a href="https://www.apcoa.co.uk/" target="_blank" rel="noreferrer">APCOA website</a>
                </li>
              </ul>
            </div>
          </details>

          <details className="train-collapsible" style={{ marginTop: 12 }}>
            <summary className="train-summary-bar">✅ Why this East Midlands guide is reliable</summary>
            <div className="train-collapsible-body">
              <div style={{ border: "1px solid #e5e7eb", borderRadius: 12, padding: 12, background: "#f9fafb" }}>
                <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.5, fontSize: 13, color: "#374151" }}>
                  <li>Uses published EMA pick-up/drop-off tariffs and stay limits.</li>
                  <li>Includes Blue Badge Short Stay 1 rule and Rapid Drop Off payment deadlines.</li>
                  <li>Shows Long Stay 2 free allowance and shuttle caveat period.</li>
                  <li>Explains why recommendation changes as duration increases.</li>
                </ul>
                <div style={{ marginTop: 8, fontSize: 12, color: "#6b7280" }}>
                  Rules can change; always confirm signage and official pages before travel.
                </div>
              </div>
            </div>
          </details>

          <details className="train-collapsible" style={{ marginTop: 12 }}>
            <summary className="train-summary-bar">💡 Why East Midlands Long Stay can be free</summary>
            <div className="train-collapsible-body">
              <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.5 }}>
                <li>Long Stay 2 offers up to 60 minutes free for pick-up/drop-off.</li>
                <li>Useful for uncertain arrival times and avoiding terminal curbside pressure.</li>
                <li>After 60 minutes, charges apply at £1 per minute.</li>
              </ul>
            </div>
          </details>

          <div
            style={{
              marginTop: 14,
              border: "1px solid #bfdbfe",
              borderRadius: 14,
              padding: 14,
              background: "#eff6ff",
            }}
          >
            <div style={{ fontWeight: 800, color: "#0f172a", marginBottom: 6 }}>Plan your East Midlands Airport journey</div>
            <div style={{ fontSize: 14, color: "#1e3a8a", lineHeight: 1.55 }}>
              Need the full picture beyond drop-off fees? Use the
              {" "}
              <a href="/east-midlands-airport/live-journey-planner" style={{ fontWeight: 800 }}>
                East Midlands Airport Live Journey Planner
              </a>
              {" "}
              for leave times, disruption checks and travel comparisons.
            </div>
            <div style={{ marginTop: 10, lineHeight: 1.7, color: "#1f2937" }}>
              <div><a href="/east-midlands-airport/live-journey-planner">Live journey planner</a></div>
              <div><a href="/train-vs-taxi-east-midlands-airport">Train vs taxi to East Midlands Airport</a></div>
              <div><a href="/how-to-get-to-east-midlands-airport">How to get to East Midlands Airport</a></div>
            </div>
          </div>

          <div style={{ marginTop: 14, border: "1px solid #e5e7eb", borderRadius: 12, padding: 12, background: "#fff" }}>
            <strong>🔗 Official East Midlands references (used by this tool)</strong>
            <ul style={{ marginTop: 8, lineHeight: 1.6, marginBottom: 0 }}>
              <li>
                Pick-up and drop-off overview: <a href="https://www.eastmidlandsairport.com/parking/pick-up-and-drop-off/" target="_blank" rel="noreferrer">eastmidlandsairport.com/parking/pick-up-and-drop-off/</a>
              </li>
              <li>
                Rapid Drop Off: <a href="https://www.eastmidlandsairport.com/parking/pick-up-and-drop-off/rapid-drop-off/" target="_blank" rel="noreferrer">eastmidlandsairport.com/.../rapid-drop-off/</a>
              </li>
              <li>
                Short Stay: <a href="https://www.eastmidlandsairport.com/parking/pick-up-and-drop-off/short-stay/" target="_blank" rel="noreferrer">eastmidlandsairport.com/.../short-stay/</a>
              </li>
              <li>
                Long Stay: <a href="https://www.eastmidlandsairport.com/parking/pick-up-and-drop-off/long-stay/" target="_blank" rel="noreferrer">eastmidlandsairport.com/.../long-stay/</a>
              </li>
            </ul>
            <div style={{ marginTop: 10, fontSize: 12, color: "#6b7280" }}>
              Updated: 21st Apr 2026
            </div>
          </div>
        </div>
      )}

      <div style={{ marginTop: 40 }}>
        <ReviewsBar />
      </div>
    </div>
  );
}
